import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { META_ADS_MANAGER_LINE_HELP, metaAdsManagerGroups, metaAdsManagerLine } from '../metaAdsManager';
import { useMetaTooltipProps } from './metaTooltipBoundary';
import { metaAdsBodyTextSx, metaAdsMutedTextSx } from './metaAdsUi.styles';

const groupTitleSx = {
  ...metaAdsMutedTextSx,
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  pt: 1,
  pb: 0.25,
};

// Una fila: rótulo completo (puede envolver, alineado arriba) | valor a la derecha, misma columna.
const rowSx = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'start',
  columnGap: 1.5,
  py: 0.75,
  // El theme de MUI es `light` (su `divider` es negro y no se ve en el panel oscuro):
  // con theme oscuro usa `palette.divider`; si no, el mismo separador sutil de las filas de anuncios.
  borderTop: '1px solid',
  borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.divider : 'rgba(255,255,255,0.06)'),
};

/**
 * Columnas del Administrador de anuncios (ajuste 10) como lista tipo tabla, agrupada
 * en Alcance · Clics · Costos. Rótulos completos de Meta (`ads_manager_columns`),
 * valor exacto a la derecha («—» sin dato) y un ⓘ con la explicación en lenguaje simple.
 *
 * @param {{ stats: object|null, currency: string|null, columns?: Array, reachNote?: string|null, testId?: string }} props
 */
export function MetaAdsManagerGrid({ stats, currency, columns, reachNote = null, testId, leadGroups = [], hideMissingReach = false }) {
  // `leadGroups` (paso 2): grupos propios antes de los de Meta (p. ej. «Resultados» del detalle),
  // con la misma forma `{ key, title, rows: [{ key, label, value, help }] }` y el mismo estilo.
  // `hideMissingReach` (detalle de campaña): sin alcance (null) la fila «Alcance» no se muestra
  // (en vez de «—»); con valor, igual que siempre.
  const missingReach = (row) => hideMissingReach && (row.key === 'reach' || row.key === 'reach_last_30d') && stats?.[row.key] == null;
  const metaGroups = metaAdsManagerGroups(stats, currency, columns || undefined, reachNote)
    .map((g) => ({ ...g, rows: g.rows.filter((r) => !missingReach(r)) }));
  const groups = [...leadGroups, ...metaGroups].filter((g) => g.rows.length);
  if (!groups.length) return null;
  return (
    <Box data-testid={testId} data-source={stats?.source} sx={{ display: 'flex', flexDirection: 'column', px: 0.25 }}>
      {groups.map((group) => (
        <Box key={group.key} role="group" aria-label={group.title} data-testid={testId ? `${testId}-group-${group.key}` : undefined}>
          <Typography component="p" sx={groupTitleSx}>{group.title}</Typography>
          {group.rows.map((row) => (
            <Box key={row.key} sx={rowSx} data-testid={testId ? `${testId}-${row.key}` : undefined}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, minWidth: 0 }}>
                <Typography sx={{ ...metaAdsBodyTextSx, fontSize: '13px', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                  {row.label}
                </Typography>
                {row.help ? (
                  <Tooltip title={<span>{row.help}</span>} placement="top" arrow describeChild>
                    <IconButton
                      size="small"
                      aria-label={`Qué es ${row.label}`}
                      sx={{ p: 0.25, mt: '-1px', color: 'rgba(255,255,255,0.55)', flexShrink: 0, '&:hover': { color: 'rgba(255,255,255,0.85)' } }}
                      data-testid={testId ? `${testId}-${row.key}-help` : undefined}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
              <Typography
                sx={{
                  ...metaAdsBodyTextSx,
                  fontSize: '13px',
                  lineHeight: 1.35,
                  fontWeight: 600,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
                data-testid={testId ? `${testId}-${row.key}-value` : undefined}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/**
 * ⓘ de la línea compacta (mismo estilo que el de «Gasto total del período», `MetaContactsHelp`):
 * Tooltip de MUI con qué es CTR / CPC / CPM.
 */
export function MetaAdsManagerLineHelp({ testId }) {
  const boundaryProps = useMetaTooltipProps();
  return (
    <Tooltip
      title={<Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>{META_ADS_MANAGER_LINE_HELP.map((t) => <span key={t}>{t}</span>)}</Box>}
      placement="top"
      arrow
      describeChild
      {...boundaryProps}
    >
      <Box
        component="span"
        tabIndex={0}
        role="img"
        aria-label="Qué son CTR, CPC y CPM"
        data-testid={testId}
        sx={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'text-bottom', ml: 0.5, color: '#ABABAB', cursor: 'help', borderRadius: '50%', '&:focus-visible': { outline: '2px solid', outlineOffset: 1 } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
      </Box>
    </Tooltip>
  );
}

/**
 * Línea compacta: sin las aclaraciones entre paréntesis (entra en una línea en el ancho del panel)
 * + ⓘ al final que explica CTR / CPC / CPM. Si no entra, envuelve (sin ellipsis); el ⓘ queda
 * pegado al último valor. `showHelp` = false para no repetir el ⓘ en filas seguidas.
 */
export function MetaAdsManagerLine({ stats, currency, prefix = null, testId, showHelp = true }) {
  const text = metaAdsManagerLine(stats, currency, { short: true });
  if (!text) return null;
  return (
    <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }} data-testid={testId}>
      {prefix ? `${prefix}: ` : ''}{text}
      {showHelp ? <MetaAdsManagerLineHelp testId={testId ? `${testId}-help` : undefined} /> : null}
    </Typography>
  );
}
